import axios, { type AxiosInstance } from 'axios';

import { COMMANDAPI, getUserData, QUERYAPI } from '../constants/global';
import {
	type ISagaPagedResponse,
	type ISagaRow,
	SAGA_CODE_FILTER_KEY,
	SAGA_MAX_PAGE_SIZE,
	type SagaLevel,
} from '../interface/saga-status';

/**
 * Standalone clients for saga status. Same shape as the other `src/lib` clients: attach the
 * core JWT + PartyId, NO 401->logout interceptor (a status poll must never sign an agent out),
 * and resolve non-2xx via `validateStatus` so the caller can classify rather than throw.
 *
 * Deliberately not a Redux thunk. Polling through one would fire the global "Something went
 * wrong" notification on every failed tick and dispatch into the store every few seconds, and
 * a status read failing is not a reason to interrupt the agent.
 *
 * Reads go to the Query service and the manual replay trigger to the Command service, so each
 * gets its own base URL.
 */
const attachAuth = (client: AxiosInstance) => {
	client.interceptors.request.use((config) => {
		const { token, user } = getUserData();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		if (user?.PartyId) config.headers.PartyId = user.PartyId;
		return config;
	});
	client.interceptors.response.use((response) => {
		if (typeof response.data === 'string' && response.data) {
			try {
				response.data = JSON.parse(response.data);
			} catch {
				// Not JSON — leave as-is.
			}
		}
		return response;
	});
	return client;
};

const sagaQueryApi = attachAuth(axios.create({ baseURL: QUERYAPI }));
const sagaCommandApi = attachAuth(axios.create({ baseURL: COMMANDAPI }));

const LEVEL_PATH: Record<SagaLevel, string> = { pu: 'pu', ward: 'ward', lga: 'lga', state: 'state' };

/** Command-service controller per level, for the manual replay triggers. */
const LEVEL_CONTROLLER: Record<SagaLevel, string> = {
	pu: 'PollingUnitResult',
	ward: 'WardResult',
	lga: 'LocalGovernmentResult',
	state: 'StateResult',
};

/** Every call resolves to one of these — never a throw, never a rejected promise. */
export type SagaFetchOutcome = { reachable: true; row: ISagaRow | null } | { reachable: false; httpStatus: number | null };

export interface ISagaLookup {
	level: SagaLevel;
	electionId: string | number;
	code: string;
}

/**
 * Fetch the saga row for one entity.
 *
 * There is no lookup by `referenceId`, so correlation is by election + entity code. The code
 * filter key is level-specific (`pollingUnitCodes` / `wardCodes` / `lgaCodes` / `stateCodes`);
 * sending the wrong one is not an error — the server ignores unknown JSON properties, so the
 * filter would be dropped and an unfiltered page returned.
 */
export const getSagaState = async ({ level, electionId, code }: ISagaLookup): Promise<SagaFetchOutcome> => {
	if (!code) return { reachable: true, row: null };

	try {
		const response = await sagaQueryApi.post<ISagaPagedResponse>(
			`/api/v1/i-elect/SagaState/get-all-${LEVEL_PATH[level]}-paged`,
			{
				electionIds: [String(electionId)],
				[SAGA_CODE_FILTER_KEY[level]]: [code],
				currentStates: [],
				resultIds: [],
				pageNumber: 1,
				// Server caps this at 50 and clamps silently, so never ask for more.
				pageSize: Math.min(1, SAGA_MAX_PAGE_SIZE),
				// `sortBy`/`orderBy` are ignored by this endpoint; ordering comes from `sort[]`,
				// first element only, with lowercase field tokens.
				sort: [{ field: 'updatedat', order: 'desc' }],
			},
			{ validateStatus: () => true }
		);

		if (response.status < 200 || response.status >= 300) return { reachable: false, httpStatus: response.status };

		// Bare envelope: `data`, not `items`. Reading `items`/`totalCount` yields undefined,
		// which would render as "no saga yet" forever.
		const rows = response.data?.data ?? [];

		return { reachable: true, row: rows[0] ?? null };
	} catch {
		return { reachable: false, httpStatus: null };
	}
};

/**
 * Ask the pipeline to re-run AI extraction.
 *
 * Fire-and-forget on the server: the controller publishes a message without looking the saga
 * up, so a 202 here means "accepted for delivery", NOT "it will happen". Verified live — a
 * nonexistent entity code still returns `202 {isSuccessful: true}`. The state machine drops
 * the message unless the saga is in Processing / AwaitingIrev / Completed /
 * CompletedWithWarnings. Callers must confirm by re-reading the row, never by this result.
 */
export const triggerAiFetch = async ({
	level,
	electionId,
	code,
}: ISagaLookup): Promise<{ queued: boolean; httpStatus: number | null }> => {
	try {
		const response = await sagaCommandApi.post(
			`/api/v1/i-elect/${LEVEL_CONTROLLER[level]}/trigger-ai-fetch/${electionId}/${encodeURIComponent(code)}`,
			undefined,
			{ validateStatus: () => true }
		);

		return { queued: response.status >= 200 && response.status < 300, httpStatus: response.status };
	} catch {
		return { queued: false, httpStatus: null };
	}
};
